import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, MenuItem, Skeleton, TextField, Typography } from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import HotelIcon from "@mui/icons-material/Hotel";
import LocalTaxiIcon from "@mui/icons-material/LocalTaxi";
import HeartBrokenIcon from "@mui/icons-material/HeartBroken";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import LogoutIcon from "@mui/icons-material/Logout";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PrintIcon from "@mui/icons-material/Print";

import {
  IPD_COLORS,
  IPD_SHAPE,
  getIpdSelectMenuProps,
  ipdElevatedInputSx,
  ipdLabelSx,
  IpdButton,
  IpdDetailRow,
  IpdSectionCard,
} from "../../ipd/utils";
import { formatDateTime } from "../constants";
import { closeEmergencyVisit, getAssignable } from "../api/emergencyApi";
import {
  firePendingCharges,
  fireTransferToAdmission,
  getChargeCheck,
} from "../api/emergencyBillingApi";
import { chargeCheckCount } from "../utils/charges";
import { detailBlock, escapeHtml, printDocument } from "../utils/print";

/**
 * The six outcomes (ER-DSP-002). Each carries the consequence of choosing it,
 * because these are not interchangeable labels — one creates an inpatient
 * admission, one records a death, one produces a document for another hospital.
 */
const OUTCOMES = [
  { value: "discharged", label: "Discharged", icon: HomeIcon,
    hint: "Home, with follow-up instructions" },
  { value: "admitted", label: "Admitted", icon: HotelIcon,
    hint: "Moves to inpatient care under a ward doctor" },
  { value: "transferred", label: "Transferred", icon: LocalTaxiIcon,
    hint: "To another facility, with a written transfer note" },
  { value: "expired", label: "Expired", icon: HeartBrokenIcon,
    hint: "Records the time and cause of death" },
  { value: "lwbs", label: "Left Without Being Seen", icon: DirectionsRunIcon,
    hint: "Left before any assessment" },
  { value: "ama", label: "Against Medical Advice", icon: LogoutIcon,
    hint: "Chose to leave against the provider's advice" },
];

const LABEL = Object.fromEntries(OUTCOMES.map((o) => [o.value, o.label]));

/**
 * The IPD registration referral an Admitted disposition created (ER-DSP-004).
 *
 * Closing as Admitted refers the patient into the IPD registration queue —
 * the same queue an OPD referral lands in — and the admission desk completes
 * registration, chooses the bed and creates the admission from the wizard.
 * The status here is read live from the queue, so the desk finishing the job
 * shows up on the emergency record without it being touched.
 */
function ReferralLink({ visit }) {
  const r = visit.ipd_referral;

  if (!r) {
    // Closed as Admitted with nothing to show for it. The backend writes both
    // in one transaction, so this means the queue row has been removed since —
    // worth saying plainly rather than rendering nothing.
    return (
      <Alert severity="warning" sx={{ mt: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}>
        This visit closed as Admitted but the patient is no longer in the IPD registration
        queue and may not have been admitted. Check the patient's record.
      </Alert>
    );
  }

  const registered = r.status === "admitted";

  return (
    <Box
      sx={{
        mt: 2,
        p: 1.75,
        borderRadius: `${IPD_SHAPE.radiusMd}px`,
        bgcolor: IPD_COLORS.cardBg,
        border: `1px solid ${IPD_COLORS.border}`,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
        <HotelIcon sx={{ fontSize: 17, color: IPD_COLORS.primary }} />
        <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>
          IPD registration referral
        </Typography>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <IpdDetailRow label="Referred at" value={formatDateTime(r.queued_at)} />
        <IpdDetailRow label="Referred to" value={r.referred_doctor_name} />
        <IpdDetailRow
          label="Status"
          value={registered ? "Registered and admitted" : "Awaiting registration"}
        />
      </Box>
      <Typography sx={{ mt: 1, fontSize: 12, color: IPD_COLORS.textMuted }}>
        {registered
          ? "The admission desk has completed the patient's inpatient registration."
          : "The patient sits at the top of the admission desk's queue — the desk completes " +
            "registration, chooses the ward bed and creates the inpatient admission."}
      </Typography>
      <Typography sx={{ mt: 1, fontSize: 12, color: IPD_COLORS.textMuted }}>
        This emergency visit is a separate record and stays as it is; it was not converted into
        the admission.
      </Typography>
    </Box>
  );
}

export default function DispositionCard({ visit, onClosed }) {
  const closed = visit.status === "closed";
  const d = visit.disposition_detail;

  const [outcome, setOutcome] = useState("");
  const [reason, setReason] = useState("");
  const [causeOfDeath, setCauseOfDeath] = useState("");
  const [receivingFacility, setReceivingFacility] = useState("");
  const [transferNote, setTransferNote] = useState("");
  const [admittingDoctorId, setAdmittingDoctorId] = useState("");
  const [doctors, setDoctors] = useState(null);
  const [doctorsError, setDoctorsError] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [unbilled, setUnbilled] = useState(0);

  // How much of the visit has not reached billing, said before closing. It
  // never blocks the close (ER-DSP-016): the charges stay in the charge check.
  useEffect(() => {
    if (closed) return;
    getChargeCheck(visit.id)
      .then((check) => setUnbilled(chargeCheckCount(check)))
      .catch(() => setUnbilled(0));
  }, [visit.id, closed]);

  // The ward's doctors are only fetched once Admitted is actually chosen —
  // five of the six outcomes never need them, and the card is on screen for
  // the whole visit.
  const loadDoctors = useCallback(async () => {
    setDoctorsError("");
    try {
      const options = await getAssignable();
      setDoctors(options?.doctors || []);
    } catch (e) {
      setDoctors(null);
      setDoctorsError(e?.message || "Could not load the list of doctors");
    }
  }, []);

  useEffect(() => {
    if (outcome === "admitted" && doctors === null && !doctorsError) loadDoctors();
  }, [outcome, doctors, doctorsError, loadDoctors]);

  const printTransferNote = () => {
    const opened = printDocument({
      title: "Inter-facility Transfer Note",
      subtitle: `${visit.display_name} · ${visit.visit_number}`,
      body: `
        <h2>Patient</h2>
        ${detailBlock([
          ["Name", visit.display_name],
          ["Visit number", visit.visit_number],
          ["Hospital identifier", visit.patient_id],
          ["Age / gender", `${visit.age ?? "not recorded"} / ${visit.gender || "—"}`],
          ["Chief complaint", visit.chief_complaint],
        ])}
        <h2>Transfer</h2>
        ${detailBlock([
          ["Receiving facility", d.receiving_facility],
          ["Decided at", formatDateTime(d.decided_at)],
          ["By", d.decided_by_name],
          ["Reason", d.reason],
        ])}
        ${d.transfer_note
          ? `<h2>Clinical note</h2><p>${escapeHtml(d.transfer_note)}</p>` : ""}
        <p class="note">
          Documentation only — the receiving facility does not acknowledge this
          transfer in the sending hospital's system.
        </p>`,
    });
    if (!opened) setError("The print window was blocked — allow pop-ups for this site.");
  };

  /* ------------------------------------------------------------- closed */
  if (closed && d) {
    return (
      <IpdSectionCard title="Outcome">
        <Alert
          icon={<CheckCircleOutlineIcon />}
          severity="success"
          sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}
        >
          <strong>Visit closed — {d.outcome_label}</strong>
          <Typography sx={{ fontSize: 12.5, mt: 0.25 }}>
            {formatDateTime(d.decided_at)}
            {d.decided_by_name ? ` by ${d.decided_by_name}` : ""}. A closed visit is never
            reopened — corrections are edits to the record.
          </Typography>
        </Alert>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <IpdDetailRow label="Reason" value={d.reason} />
          {d.cause_of_death && <IpdDetailRow label="Cause of death" value={d.cause_of_death} />}
          {d.time_of_death && (
            <IpdDetailRow label="Time of death" value={formatDateTime(d.time_of_death)} />
          )}
          {d.receiving_facility && (
            <IpdDetailRow label="Receiving facility" value={d.receiving_facility} />
          )}
        </Box>

        {d.transfer_note && (
          <Box sx={{ mt: 1.5 }}>
            <Typography sx={{ ...ipdLabelSx, mb: 0.5 }}>Transfer note</Typography>
            <Typography sx={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{d.transfer_note}</Typography>
          </Box>
        )}

        {d.outcome === "admitted" && <ReferralLink visit={visit} />}

        {d.outcome === "expired" && (
          <Typography sx={{ mt: 2, fontSize: 12, color: IPD_COLORS.textMuted }}>
            No death certificate is produced and no mortuary process is started — both are
            outside this phase.
          </Typography>
        )}

        {d.outcome === "transferred" && (
          <IpdButton
            variant="outlined"
            size="small"
            startIcon={<PrintIcon />}
            sx={{ mt: 2 }}
            onClick={printTransferNote}
          >
            Print transfer note
          </IpdButton>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}>
            {error}
          </Alert>
        )}
      </IpdSectionCard>
    );
  }

  /* --------------------------------------------------------------- open */
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const payload = { outcome, reason: reason.trim() };
      if (outcome === "expired") payload.cause_of_death = causeOfDeath.trim();
      if (outcome === "admitted") payload.admitting_doctor_id = admittingDoctorId;
      if (outcome === "transferred") {
        payload.receiving_facility = receivingFacility.trim();
        payload.transfer_note = transferNote.trim();
      }
      // The server's message is the accurate one — it knows whether a bed was
      // released, what housekeeping did with it, and who the patient was
      // referred under. Guessing any of that here gets it wrong for a patient
      // who never had a bed.
      const { message } = await closeEmergencyVisit(visit.id, payload);
      // Closing sends the visit's charges to billing (ER-DSP-016), without
      // waiting: a billing outage must not hold the patient's departure.
      firePendingCharges(visit.id);
      if (outcome === "admitted") fireTransferToAdmission(visit.id);
      await onClosed(message);
    } catch (e) {
      setError(e?.message || "Could not close the visit");
    } finally {
      setBusy(false);
    }
  };

  const ready =
    outcome &&
    reason.trim() &&
    (outcome !== "expired" || causeOfDeath.trim()) &&
    (outcome !== "admitted" || admittingDoctorId) &&
    // The server refuses this too; disabling the button as well means the
    // doctor finds out before filling in a reason, not after.
    (outcome !== "discharged" || Boolean(visit.discharge_summary)) &&
    (outcome !== "transferred" || receivingFacility.trim());

  return (
    <IpdSectionCard
      title="Close the visit"
      subtitle="Every visit ends with exactly one outcome. Closing frees the bed and cannot be undone."
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Box
        sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1, mb: 2 }}
        role="radiogroup"
        aria-label="Disposition outcome"
      >
        {OUTCOMES.map((o) => {
          const active = outcome === o.value;
          const Icon = o.icon;
          return (
            <Box
              key={o.value}
              component="button"
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setOutcome(o.value)}
              sx={{
                display: "flex", alignItems: "flex-start", gap: 1.25,
                px: 1.5, py: 1.25, cursor: "pointer", textAlign: "left",
                fontFamily: "inherit",
                borderRadius: `${IPD_SHAPE.radiusMd}px`,
                bgcolor: active ? IPD_COLORS.selectedBg : IPD_COLORS.cardBg,
                border: `1px solid ${active ? IPD_COLORS.selectedBorder : IPD_COLORS.border}`,
                transition: "all .12s ease",
                "&:hover": { borderColor: IPD_COLORS.primary },
                "&:focus-visible": { outline: `2px solid ${IPD_COLORS.primary}`, outlineOffset: 2 },
              }}
            >
              <Icon sx={{ fontSize: 17, mt: 0.2, color: active ? IPD_COLORS.primary : IPD_COLORS.textMuted }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: active ? 700 : 600 }}>
                  {o.label}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: IPD_COLORS.textSecondary }}>
                  {o.hint}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: "grid", gap: 1.5 }}>
        <TextField
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          label="Reason"
          placeholder="Why this outcome was decided…"
          size="small"
          fullWidth
          multiline
          minRows={2}
          required
          sx={ipdElevatedInputSx}
        />

        {outcome === "expired" && (
          <>
            <TextField
              value={causeOfDeath}
              onChange={(e) => setCauseOfDeath(e.target.value)}
              label="Cause of death"
              size="small"
              fullWidth
              required
              sx={ipdElevatedInputSx}
              helperText="The exact time of death is recorded automatically."
            />
            <Typography sx={{ fontSize: 12, color: IPD_COLORS.textMuted }}>
              No death certificate is produced and no mortuary process starts — both are
              outside this phase.
            </Typography>
          </>
        )}

        {outcome === "transferred" && (
          <>
            <TextField
              value={receivingFacility}
              onChange={(e) => setReceivingFacility(e.target.value)}
              label="Receiving facility"
              size="small"
              fullWidth
              required
              sx={ipdElevatedInputSx}
            />
            <TextField
              value={transferNote}
              onChange={(e) => setTransferNote(e.target.value)}
              label="Transfer note"
              placeholder="Condition on transfer, treatment given, reason for transfer…"
              size="small"
              fullWidth
              multiline
              minRows={3}
              sx={ipdElevatedInputSx}
              helperText="Printable. The receiving side does not acknowledge it in this system."
            />
          </>
        )}

        {outcome === "admitted" && (
          <>
            {doctorsError ? (
              <Alert
                severity="error"
                sx={{ borderRadius: `${IPD_SHAPE.radiusMd}px` }}
                action={
                  <IpdButton
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setDoctorsError("");
                      loadDoctors();
                    }}
                  >
                    Retry
                  </IpdButton>
                }
              >
                {doctorsError}
              </Alert>
            ) : doctors === null ? (
              <Skeleton variant="rounded" height={44} />
            ) : (
              <Box>
                <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>
                  Admitting doctor <span style={{ color: IPD_COLORS.error.text }}>*</span>
                </Typography>
                <TextField
                  select
                  value={admittingDoctorId}
                  onChange={(e) => setAdmittingDoctorId(e.target.value)}
                  size="small"
                  fullWidth
                  sx={ipdElevatedInputSx}
                  SelectProps={{ displayEmpty: true, MenuProps: getIpdSelectMenuProps() }}
                  inputProps={{ "aria-label": "Admitting doctor" }}
                  helperText={
                    doctors.length
                      ? "The doctor the patient is referred to for admission. Chosen here — the Emergency Provider does not carry across."
                      : "No doctors are listed at this facility, so the patient cannot be admitted from here."
                  }
                >
                  <MenuItem value="" disabled>
                    {doctors.length
                      ? "Name the admitting doctor"
                      : "No doctors at this facility"}
                  </MenuItem>
                  {doctors.map((doc) => (
                    <MenuItem key={doc.id} value={doc.id}>
                      {doc.name}
                      {doc.specialization ? ` — ${doc.specialization}` : ""}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            )}
            <Alert severity="info" sx={{ borderRadius: `${IPD_SHAPE.radiusMd}px` }}>
              This refers the patient to the IPD registration queue — the same queue as OPD
              referrals, at the top of it. The admission desk completes registration, chooses
              the ward bed and creates the inpatient admission from there.
            </Alert>
          </>
        )}

        {outcome === "discharged" && !visit.discharge_summary && (
          <Alert severity="warning" sx={{ borderRadius: `${IPD_SHAPE.radiusMd}px` }}>
            A discharged patient goes home with a written summary. Draft it in the
            Discharge summary card above before closing.
          </Alert>
        )}

        {visit.is_mlc && (visit.injuries || []).length === 0 && (
          <Alert severity="warning" sx={{ borderRadius: `${IPD_SHAPE.radiusMd}px` }}>
            This is a medico-legal case with no injury report. Record the injuries above before
            closing — the only exemption is a patient brought in dead.
          </Alert>
        )}

        {unbilled > 0 && (
          <Alert severity="warning" sx={{ borderRadius: `${IPD_SHAPE.radiusMd}px` }}>
            {unbilled} charge(s) have not reached billing yet. You can still close the visit —
            they stay in the Charges tab's charge check.
          </Alert>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <IpdButton size="large" onClick={submit} loading={busy} disabled={!ready}>
            Record disposition &amp; close visit
          </IpdButton>
        </Box>
      </Box>
    </IpdSectionCard>
  );
}

export { LABEL as DISPOSITION_LABEL };
